import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import Button from '@mui/material/Button'
import ContainerPage from '../../../components/ContainerPage/ContainerPage'
import {
  loadProcessedHistory,
  refetchResults,
  setSelectedPaymentId,
} from '../../../features/bulkProcessing/bulkProcessing'
import {
  refundPaymentCharge,
  voidPaymentCharge,
  changePaymentStatusFetch,
} from '../../../features/payment/paymentSlice'
import { showSnackBar } from '../../../components/Snackbar/Snackbar'
import MarkPaymentStatusModal from '../../../components/PaymentModals/MarkPaymentStatusModal'
import { businessTimeZone, formatBusinessDate } from '../../../utils/date'
import { downloadCsv } from '../../../utils/csv'
import KpiCards from './KpiCards'
import FiltersBar from './FiltersBar'
import StatusTabs from './StatusTabs'
import PaymentsHistoryTable from './PaymentsHistoryTable'
import DetailPanel from './DetailPanel'
import SettlementForecastFooter from './SettlementForecastFooter'
import { filterHistoryRows, getStatusTabCounts, getProcessorOptions, buildClientKpis } from './helpers'
import DateRangePresets from '../shared/DateRangePresets'
import { PROCESSED_PRESETS, CUSTOM_PRESET_KEY, getPresetRange } from '../shared/presetRanges'
import { selectFeatureFlag } from '../../../features/featureFlags/selectors'
import { PARKED_CHARGE_VISIBLE } from '../../../config/featureFlagKeys'
import { useUiKit } from '../../../components/UiKit/UiKitContext'
import { failureOf } from '../../../utils/errorHelpers'

// Default range is TODAY (business day) — operators open this screen to answer "what
// happened to today's money"; the old 30-day default buried that under weeks of
// history. Wider ranges are one preset chip (or a calendar edit) away.
// .startOf('day') zeroes the time-of-day so the Calendar picker (which defaults
// to timezone="UTC") displays the same calendar date we actually query — otherwise
// a late-ET moment can round up to the next UTC day in the picker.
const defaultRange = () => {
  const today = dayjs().tz(businessTimeZone).startOf('day')
  return { from: today, to: today }
}

const ProcessedPayments = ({ embedded = false, basis = 'submitted' }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { button: kitButton, confirm: kitConfirm } = useUiKit()
  const { history, historyLoading, historyError, refetching, selectedPaymentId } = useSelector(
    (state) => state.bulkProcessing
  )
  const parkedChargeVisible = useSelector(selectFeatureFlag(PARKED_CHARGE_VISIBLE))

  const [{ from, to }, setRange] = useState(defaultRange)
  // Which quick date-range chip is active — the default range IS today; a manual
  // calendar edit flips it to 'custom'.
  const [datePreset, setDatePreset] = useState('today')
  const [search, setSearch] = useState('')
  const [processor, setProcessor] = useState('all')
  const [paymentType, setPaymentType] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  const fetchHistory = async (range = { from, to }, { active } = {}) => {
    await dispatch(
      loadProcessedHistory({
        from: range.from.format('YYYY-MM-DD'),
        to: range.to.format('YYYY-MM-DD'),
        basis,
      })
    )
    if (active && !active.current) return
    setLastUpdatedAt(dayjs())
  }

  useEffect(() => {
    const active = { current: true }
    fetchHistory(undefined, { active }).finally(() => {
      if (active.current) setInitialLoadDone(true)
    })
    return () => {
      active.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (historyError) showSnackBar('error', historyError)
  }, [historyError])

  const rows = history?.rows || []
  const settlementForecast = history?.settlementForecast || []

  // Rows filtered by every NON-status facet. KPI cards and the per-tab counts read
  // from THIS set (not the raw response) so they follow the applied filters — the
  // status tab itself is navigation between the buckets those numbers describe, so
  // it deliberately does not feed back into them.
  const facetRows = useMemo(
    () => filterHistoryRows(rows, { tabKey: 'all', search, processor, paymentType }),
    [rows, search, processor, paymentType]
  )
  const filteredRows = useMemo(
    () => filterHistoryRows(facetRows, { tabKey: statusFilter }),
    [facetRows, statusFilter]
  )
  const kpis = useMemo(() => buildClientKpis(facetRows), [facetRows])
  const counts = useMemo(() => getStatusTabCounts(facetRows), [facetRows])
  const processorOptions = useMemo(() => getProcessorOptions(rows), [rows])
  const selectedRow = useMemo(() => rows.find((row) => row.paymentId === selectedPaymentId) || null, [
    rows,
    selectedPaymentId,
  ])

  const handleRefreshNow = () => fetchHistory()

  // A From/To change must re-query — the range is a server-side filter, not a
  // client-side one, so without a refetch the table keeps showing the old range.
  // A manual calendar edit also demotes the active quick-range chip to 'custom'.
  const handleChangeFrom = (value) => {
    const next = { from: value, to }
    setDatePreset(CUSTOM_PRESET_KEY)
    setRange(next)
    fetchHistory(next)
  }
  const handleChangeTo = (value) => {
    const next = { from, to: value }
    setDatePreset(CUSTOM_PRESET_KEY)
    setRange(next)
    fetchHistory(next)
  }

  const handleSelectPreset = (key) => {
    setDatePreset(key)
    // 'custom' imposes no range — it just hands control back to the calendars.
    const preset = getPresetRange(key)
    if (!preset) return
    setRange(preset)
    fetchHistory(preset)
  }

  const handleExport = () => {
    if (!filteredRows.length) {
      showSnackBar('alert', 'Nothing to export.')
      return
    }
    downloadCsv(
      `processed-payments-${from.format('YYYY-MM-DD')}_${to.format('YYYY-MM-DD')}.csv`,
      [
        { label: 'Client', value: (r) => r.clientName },
        { label: 'Client Code', value: (r) => r.clientCode },
        { label: 'Amount', value: (r) => Number(r.amount || 0).toFixed(2) },
        { label: 'Method', value: (r) => (r.method ? `${r.method.type || ''} ${r.method.last4 ? `••${r.method.last4}` : ''}`.trim() : '') },
        { label: 'Processor', value: (r) => r.processor || '' },
        { label: 'Status', value: (r) => r.statusLabel || r.status },
        { label: 'Card Verification', value: (r) => (r.isVerificationPayment ? (r.cardVerification || 'yes') : '') },
        { label: 'Processed On', value: (r) => (r.processedOn ? formatBusinessDate(r.processedOn) : '') },
        { label: 'Settled On', value: (r) => (r.settledOn ? formatBusinessDate(r.settledOn) : '') },
      ],
      filteredRows
    )
  }

  const handleClearFilters = () => {
    setSearch('')
    setProcessor('all')
    setPaymentType('all')
    setStatusFilter('all')
  }

  const handleRowClick = (row) => dispatch(setSelectedPaymentId(row.paymentId))
  const handleCloseDetail = () => dispatch(setSelectedPaymentId(null))

  const handleRefetch = (paymentId) => dispatch(refetchResults({ paymentIds: [paymentId] }))

  // Per-payment money actions — dispatch, surface the result, and refresh the list.
  const runPaymentAction = async (thunkResult, successMsg) => {
    const res = await thunkResult
    if (res.error) {
      showSnackBar('error', res.payload || 'Action failed')
      return false
    }
    showSnackBar('success', successMsg)
    await fetchHistory()
    return true
  }

  // The amount comes from the panel, which pre-fills it with the SERVER's cap
  // (row.reversal.maxRefund = capturedAmount) and only lets the operator edit it when the backend
  // says the gateway supports a partial refund. The `row.reversal?.maxRefund ?? row.amount`
  // fallback is for an older payload that carries no cap; row.amount alone would OVERSTATE the
  // refundable amount for any payment that took a partial payment-link payment first.
  const handleRefund = (row, amount) =>
    runPaymentAction(
      dispatch(refundPaymentCharge({
        paymentId: row.paymentId,
        refundAmount: amount ?? row.reversal?.maxRefund ?? row.amount,
        refundMemo: '',
      })),
      'Refund submitted'
    )

  const handleVoid = (row) => runPaymentAction(dispatch(voidPaymentCharge({ paymentId: row.paymentId })), 'Charge voided')

  const runPaymentActionOrThrow = async (thunkResult, successMsg) => {
    const res = await thunkResult
    if (res.error) throw failureOf(res, 'Action failed')
    showSnackBar('success', successMsg)
    await fetchHistory()
  }

  const refundOrThrow = (row, amount) =>
    runPaymentActionOrThrow(
      dispatch(refundPaymentCharge({ paymentId: row.paymentId, refundAmount: amount, refundMemo: '' })),
      'Refund submitted'
    )

  const voidOrThrow = (row) => runPaymentActionOrThrow(dispatch(voidPaymentCharge({ paymentId: row.paymentId })), 'Charge voided')

  // Mark paid/failed goes through a confirmation modal that suggests processedAt =
  // the payment's due date (editable) and collects optional transaction id / reason
  // fields — never a silent one-click money-state flip.
  const [markModal, setMarkModal] = useState(null)
  const handleMarkStatus = (row, value) => setMarkModal({ row, mode: value })
  const handleConfirmMark = (fields) => {
    const { row, mode } = markModal
    if (kitButton) {
      return runPaymentAction(
        dispatch(changePaymentStatusFetch({ paymentId: row.paymentId, value: mode, ...fields })),
        `Marked as ${mode}`
      ).then((done) => { if (!done) throw new Error('not marked') })
    }
    setMarkModal(null)
    return runPaymentAction(
      dispatch(changePaymentStatusFetch({ paymentId: row.paymentId, value: mode, ...fields })),
      `Marked as ${mode}`
    )
  }

  // Route into the client's existing Contact tabs (profile / emails / sms), which
  // already host the real compose/send UIs. 'call' has no telephony feature in the
  // platform, so it falls back to the profile.
  const handleContact = (type, row) => {
    if (!row?.userId) {
      showSnackBar('alert', 'No client record linked to this payment.')
      return
    }
    const tabByType = { email: 'emails', text: 'sms' }
    const tab = tabByType[type]
    navigate(`/contact/${row.userId}${tab ? `?activeTab=${tab}` : ''}`)
  }

  // Retry / Request-New-Card: real rescheduling is the failure-recovery workstream.
  // Until then, drop the operator onto the client's Tasks tab to follow up.
  //
  // NOTE: no `needsReview` branch here on purpose. The backend's resolveNextAction returns
  // an action only for 'declined' and 'returned' (null otherwise), and the table renders the
  // button solely off `row.nextAction` — so a parked row has no action button and this
  // handler is never reached for one. Operators open a parked row by clicking it (which
  // shows reviewReason/reviewDetail in the detail panel) or via the Needs Review filter.
  const handleNextAction = (row) => {
    if (row?.userId) navigate(`/contact/${row.userId}?activeTab=tasks`)
    else showSnackBar('alert', 'No client record linked to this payment.')
  }

  const showInitialLoading = historyLoading && !initialLoadDone

  const body = (
      <div className="flex flex-col gap-6" data-testid="processed-payments-screen">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {!embedded ? (
            <div>
              <h1 className="text-xl font-semibold text-text-primary">Processed Payments</h1>
              <p className="text-sm text-text-secondary mt-1">
                View the status and history of all payments that have been processed.
              </p>
            </div>
          ) : (
            <p className="text-sm text-text-secondary">Status and history of all processed payments.</p>
          )}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-text-secondary" data-testid="last-updated-label">
                {lastUpdatedAt ? `Last updated ${lastUpdatedAt.format('h:mm A')}` : 'Last updated —'}
              </p>
              <p className="text-[11px] text-text-secondary">Auto-refresh off — use Refresh Now</p>
            </div>
            <Button variant="outlined" onClick={handleExport} data-testid="export-btn">
              Export
            </Button>
            <Button
              variant="contained"
              onClick={handleRefreshNow}
              disabled={historyLoading}
              data-testid="refresh-now-btn"
            >
              {historyLoading ? 'Refreshing…' : 'Refresh Now'}
            </Button>
          </div>
        </div>

        <KpiCards kpis={kpis} />

        <DateRangePresets presets={PROCESSED_PRESETS} active={datePreset} onSelect={handleSelectPreset} />

        <FiltersBar
          from={from}
          to={to}
          onChangeFrom={handleChangeFrom}
          onChangeTo={handleChangeTo}
          search={search}
          onChangeSearch={setSearch}
          processor={processor}
          onChangeProcessor={setProcessor}
          processorOptions={processorOptions}
          paymentType={paymentType}
          onChangePaymentType={setPaymentType}
          statusFilter={statusFilter}
          onChangeStatusFilter={setStatusFilter}
          onMoreFilters={() => showSnackBar('alert', 'More Filters — coming soon')}
          onClear={handleClearFilters}
        />

        <StatusTabs activeTab={statusFilter} counts={counts} onChange={setStatusFilter} />

        <PaymentsHistoryTable
          rows={filteredRows}
          loading={showInitialLoading}
          totalRows={rows.length}
          onRowClick={handleRowClick}
          onNextAction={handleNextAction}
          showReviewDetail={parkedChargeVisible}
        />

        <SettlementForecastFooter forecast={settlementForecast} />

        <DetailPanel
          open={!!selectedPaymentId}
          row={selectedRow}
          refetching={refetching}
          onClose={handleCloseDetail}
          onRefetch={handleRefetch}
          onContact={handleContact}
          onRefund={kitConfirm ? refundOrThrow : handleRefund}
          onVoid={kitConfirm ? voidOrThrow : handleVoid}
          onMarkStatus={handleMarkStatus}
        />

        {markModal && (
          <MarkPaymentStatusModal
            open={!!markModal}
            mode={markModal.mode === 'failed' ? 'failed' : 'cleared'}
            payment={{ dueDate: markModal.row?.dueDate, amount: markModal.row?.amount }}
            onClose={() => setMarkModal(null)}
            onConfirm={handleConfirmMark}
          />
        )}
      </div>
  )

  return embedded ? body : <ContainerPage>{body}</ContainerPage>
}

export default ProcessedPayments
